import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './Auth.css';
import './PhoneLogin.css';

const PhoneLogin = () => {
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState('+27');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';

  const formatPhoneNumber = (value) => {
    // Always keep +27 prefix
    if (!value.startsWith('+27')) {
      return '+27';
    }
    // Remove any non-digit characters except +
    const cleaned = value.replace(/[^\d+]/g, '');
    // Limit to +27 + 9 digits
    return cleaned.substring(0, 12);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    
    if (phoneNumber.length !== 12) {
      setError('Please enter a valid South African phone number');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/auth/phone/send-otp`, { phoneNumber });
      setStep('otp');
      setResendTimer(60);
      
      // Start countdown
      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Send OTP error:', err);
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    
    if (otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/auth/phone/verify-otp`, {
        phoneNumber,
        otp
      });
      
      // Check if account is pending approval
      if (response.data.role === 'PENDING_APPROVAL' || !response.data.token) {
        setError('Account setup incomplete. Please complete your profile to continue.');
        setStep('phone');
        setOtp('');
      } else {
        login(response.data.token, response.data.userId, response.data.emailVerified);
        
        // Check if user has a profile before navigating
        try {
          const profileCheck = await axios.get(
            `${API_URL}/profiles/check`,
            {
              headers: {
                'Authorization': `Bearer ${response.data.token}`
              }
            }
          );
          
          console.log('Profile check:', profileCheck.data);
          
          if (profileCheck.data.hasProfile) {
            navigate('/discover');
          } else {
            // Redirect to profile setup if no profile exists
            navigate('/profile-setup');
          }
        } catch (profileErr) {
          console.error('Profile check error:', profileErr);
          // If profile check fails, assume no profile and redirect to setup
          navigate('/profile-setup');
        }
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setError('');
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/auth/phone/send-otp`, { phoneNumber });
      setResendTimer(60);
      
      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError(err.response?.data?.error || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="phone-login-header">
          <div className="phone-login-icon">📱</div>
          <h2>{step === 'phone' ? 'Phone Sign In' : 'Verify Your Number'}</h2>
          <p className="phone-login-subtitle">
            {step === 'phone' 
              ? 'Enter your phone number to receive a verification code' 
              : 'Enter the code we sent to your phone'}
          </p>
        </div>
        
        {step === 'phone' ? (
          <form onSubmit={handleSendOtp}>
            <div className="phone-input-group">
              <label className="phone-input-label">Phone Number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder="+27812345678"
                required
                disabled={loading}
                maxLength={12}
                className="phone-input"
              />
              <small className="phone-input-hint">🇿🇦 South African numbers only (+27)</small>
            </div>
            
            {error && (
              <div className="error-box">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={loading || phoneNumber.length !== 12}
              className="btn-send-code"
            >
              {loading ? '📤 Sending...' : '📨 Send Verification Code'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp}>
            <div className="phone-number-display">
              <p>
                📲 Code sent to<br/>
                <strong>{phoneNumber}</strong>
              </p>
            </div>
            
            <div className="phone-input-group">
              <label className="otp-label-center">Enter 6-Digit Code</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').substring(0, 6))}
                placeholder="• • • • • •"
                required
                disabled={loading}
                maxLength={6}
                autoFocus
                className="otp-input"
              />
            </div>
            
            {error && (
              <div className="error-box">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={loading || otp.length !== 6}
              className="btn-verify"
            >
              {loading ? '⏳ Verifying...' : '✓ Verify & Sign In'}
            </button>
            
            <div className="resend-container">
              {resendTimer > 0 ? (
                <p className="resend-timer">
                  ⏱️ Resend code in <strong>{resendTimer}s</strong>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading}
                  className="btn-resend"
                >
                  🔄 Resend Code
                </button>
              )}
            </div>
            
            <button
              type="button"
              onClick={() => {
                setStep('phone');
                setOtp('');
                setError('');
              }}
              className="btn-change-number"
            >
              ← Change Phone Number
            </button>
          </form>
        )}
        
        <div className="phone-login-footer">
          <Link to="/login">← Back to Login Options</Link>
        </div>
      </div>
    </div>
  );
};

export default PhoneLogin;
